import * as Sentry from "@sentry/react";
import { Capacitor } from "@capacitor/core";
import { Clipboard as CapacitorClipboard } from "@capacitor/clipboard";

import { CLIPBOARD_INVITE_PREFIX, CLIPBOARD_INVITE_REGEX, STORAGE_KEYS } from "@/lib/api/config";
import { readClipboardText } from "@/utils/clipboardText";

const CLIPBOARD_WRITE_TIMEOUT_MS = 500;

export const getStoredInviteCode = (): string | undefined => {
  if (typeof window === "undefined") {
    return undefined;
  }
  const stored = window.localStorage.getItem(STORAGE_KEYS.INVITE_CODE);
  const trimmed = stored?.trim();
  return trimmed || undefined;
};

export const setStoredInviteCode = (code: string) => {
  if (typeof window === "undefined") {
    return;
  }
  const trimmed = code.trim();
  if (!trimmed) {
    return;
  }
  window.localStorage.setItem(STORAGE_KEYS.INVITE_CODE, trimmed);
};

export const clearStoredInviteCode = () => {
  if (typeof window === "undefined") {
    return;
  }
  window.localStorage.removeItem(STORAGE_KEYS.INVITE_CODE);
};

// Writes `stasht-invite:<CODE>` to the system clipboard before the user leaves
// Safari for the App Store. Must be called inside a user gesture — iOS Safari
// rejects clipboard writes outside one. Timeout-bounded so a hung clipboard
// promise can't strand the user mid-navigation.
export const writeInviteCodeToClipboard = async (code: string): Promise<boolean> => {
  const trimmed = code.trim().toUpperCase();
  if (!trimmed) return false;
  if (typeof navigator === "undefined" || !navigator.clipboard?.writeText) return false;

  const payload = `${CLIPBOARD_INVITE_PREFIX}${trimmed}`;

  const timeout = new Promise<false>(resolve => {
    setTimeout(() => resolve(false), CLIPBOARD_WRITE_TIMEOUT_MS);
  });

  const write = navigator.clipboard
    .writeText(payload)
    .then(() => true)
    .catch(error => {
      Sentry.captureException(error, { tags: { errorSource: "invite-clipboard-write" } });
      return false;
    });

  return Promise.race([write, timeout]);
};

// Reads the system clipboard once on first app launch, matches the handoff
// contract, and persists the captured code via `setStoredInviteCode` so the
// existing signup flow picks it up unchanged. Clears the clipboard on match so
// a reinstall can't re-apply a stale code. Returns the captured code or null.
export const consumeInviteCodeFromClipboard = async (): Promise<string | null> => {
  let text: string | null = null;
  try {
    text = await readClipboardText();
  } catch (error) {
    Sentry.captureException(error, { tags: { errorSource: "invite-clipboard-read" } });
    return null;
  }

  if (!text) return null;

  const match = text.trim().match(CLIPBOARD_INVITE_REGEX);
  if (!match) return null;

  const captured = match[1];
  setStoredInviteCode(captured);

  if (Capacitor.isNativePlatform()) {
    try {
      await CapacitorClipboard.write({ string: "" });
    } catch {
      // non-fatal: clearing is best-effort hygiene
    }
  }

  return captured;
};
